import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux"; // Import useDispatch and useSelector from Redux
import {
  fetchUserNotifications,
  fetchVendorNotifications,
  sendNotificationAsync,
} from "./../slices/notificationsSlice"; // Import async actions from the slice
import "./notification.css";
import noDataImage from "./../assets/icons/nodataimage.svg";
import Swal from "sweetalert2"; // Import SweetAlert
import { ShimmerDiv, ShimmerTable } from "shimmer-effects-react";

const Notifications = () => {
  const dispatch = useDispatch();
  const { users, vendors, status, error } = useSelector(
    (state) => state.notifications
  ); // Use useSelector to get notifications state

  const [view, setView] = useState("userNotifications"); // Default selected as User Notifications
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [currentPageUser, setCurrentPageUser] = useState(1);
  const [currentPageVendor, setCurrentPageVendor] = useState(1);
  const [itemsPerPage] = useState(5); // Set items per page for pagination

  const handleCardClick = (title, message) => {
    Swal.fire({
      title: title || "Unnamed",
      text: message || "No content available",
      confirmButtonText: "Close",
    });
  };

  useEffect(() => {
    if (view === "userNotifications") {
      dispatch(fetchUserNotifications());
    } else {
      dispatch(fetchVendorNotifications());
    }
  }, [view, dispatch]);

  const togglePopup = () => {
    setIsPopupVisible(!isPopupVisible);
  };

  const handleSendNotification = () => {
    if (!title.trim() || !body.trim()) {
      Swal.fire({
        title: "Error",
        text: "Title and body cannot be empty",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    // Dispatch async thunk to send notification
    dispatch(sendNotificationAsync({ title, body, view })).then((action) => {
      if (action.meta.requestStatus === "fulfilled") {
        Swal.fire({
          title: "Success",
          text: "Notification sent successfully",
          icon: "success",
          confirmButtonText: "OK",
        });
        setIsPopupVisible(false);
        setTitle("");
        setBody("");
        if (view === "userNotifications") {
          dispatch(fetchUserNotifications());
        } else {
          dispatch(fetchVendorNotifications());
        }
      } else {
        Swal.fire({
          title: "Error",
          text: "Failed to send notification",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    });
  };

  // Pagination logic
  const paginate = (items, pageNumber) => {
    const startIndex = (pageNumber - 1) * itemsPerPage;
    return items.slice(startIndex, startIndex + itemsPerPage);
  };

  const handlePageChangeUser = (pageNumber) => {
    setCurrentPageUser(pageNumber);
  };

  const handlePageChangeVendor = (pageNumber) => {
    setCurrentPageVendor(pageNumber);
  };

  if (status === "loading") {
    return (
      <>
        <div className="flex items-center justify-between gap-8">
          <ShimmerDiv mode="dark" height={100} width={600} rounded={1} border={0} />
          <ShimmerDiv mode="dark" height={100} width={600} rounded={1} border={0} />
          {/* <ShimmerTitle mode="dark" line={3} gap={8} width={500} /> */}
        </div>
        <ShimmerTable mode="dark" row={4} col={2} border={0} borderColor={"#cbd5e1"} rounded={0.25} rowGap={16} colPadding={[10, 5, 10, 5]} />
      </>
    );
  }

  if (status === "failed") {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="notifications-container  ">
      <div className="buttons-container ">
        <button
          className={`btn1 ${view === "userNotifications" ? "active-btn" : "de-btn"
            }`}
          onClick={() => setView("userNotifications")}
        >
          Send to Users
        </button>
        <button
          className={`btn ${view === "sendToVendor" ? "active-btn" : "de-btn"
            } ml-4`}
          onClick={() => setView("sendToVendor")}
        >
          Send to Vendors
        </button>
      </div>

      {view === "userNotifications" ? (
        users.length === 0 ? (
          <div className="no-notifications text-center">
            <img src={noDataImage} alt="No data" className="no-data-image" />
            <h3 className="text-lg font-semibold">
              No Notifications to show right now!
            </h3>
            <p className="text-light-gray">
              Notifications will be shown here. Notifications will be shown
              here.
            </p>
            <button
              className="btn bg-yellow-400 mt-4 active-btn"
              onClick={togglePopup}
            >
              CREATE NOW
            </button>
          </div>
        ) : (
          <div className="notifications-list  ">
            {paginate(users, currentPageUser).map((entry) => (
              <div
                className="user-notification-item flex items-center p-4 mb-4 bg-secondary rounded-lg relative"
                key={entry._id}
                onClick={() => handleCardClick(entry.head, entry.message)} // Handle the click to show SweetAlert
              >
                <div className="user-notification-content">
                  <h4 className="text-xl font-semibold">
                    {entry.head || "Unnamed"}
                  </h4>
                  <p className="text-light-gray truncate-message1">
                    {entry.message || "No content available"}
                  </p>
                </div>
                <span className="absolute bottom-2 right-2 notification-date w-16 h-12 flex items-center justify-center text-white text-sm">
                  {new Date(entry.createdAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              </div>
            ))}

            <div className="pagination-container flex justify-center mt-4">
              {Array.from({
                length: Math.ceil(users.length / itemsPerPage),
              }).map((_, index) => (
                <button
                  key={index}
                  className={`pagination-btn px-3 py-2 mx-1 rounded-md border border-gray-300 ${currentPageUser === index + 1
                    ? "bg-blue-500 text-white font-semibold border-blue-500"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                    } transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  onClick={() => handlePageChangeUser(index + 1)}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </div>
        )
      ) : view === "sendToVendor" ? (
        vendors.length === 0 ? (
          <div className="no-notifications text-center">
            <img src={noDataImage} alt="No data" className="no-data-image" />
            <h3 className="text-lg font-semibold">
              No Notifications to show right now!
            </h3>
            <p className="text-light-gray">
              Notifications will be shown here. Notifications will be shown
              here.
            </p>
            <button
              className="btn bg-yellow-400 mt-4 active-btn"
              onClick={togglePopup}
            >
              CREATE NOW
            </button>
          </div>
        ) : (
          <div className="notifications-list">
            {paginate(vendors, currentPageVendor).map((entry) => (
              <div
                className="vendor-notification-item bg-secondary flex items-center justify-between p-4 mb-4 rounded-lg cursor-pointer relative"
                key={entry._id}
                onClick={() => handleCardClick(entry.head, entry.message)} // Handle the click to show SweetAlert
              >
                <div className="vendor-notification-content">
                  <h4 className="text-xl font-semibold">
                    {entry.head || "Unnamed"}
                  </h4>
                  <p className="text-light-gray truncate-message1">
                    {entry.message || "No content available"}
                  </p>
                </div>
                <span className="absolute bottom-2 right-2 notification-date w-16 h-12 flex items-center justify-center text-white text-sm">
                  {new Date(entry.createdAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              </div>
            ))}
            {/* TODO: Pagination code add appropriatly */}
            <div className="pagination-container flex justify-center mt-4">
              {Array.from({
                length: Math.ceil(vendors.length / itemsPerPage),
              }).map((_, index) => (
                <button
                  key={index}
                  className={`pagination-btn px-3 py-2 mx-1 rounded-md border border-gray-300 ${currentPageVendor === index + 1
                    ? "bg-blue-500 text-white font-semibold border-blue-500"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                    } transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  onClick={() => handlePageChangeVendor(index + 1)}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </div>
        )
      ) : null}

      <button className="fab bg-yellow-400 text-white" onClick={togglePopup}>
        ✎
      </button>

      {isPopupVisible && (
        <div className="popup-overlay" onClick={togglePopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="close-btn pl-4 left-align-button"
              onClick={togglePopup}
            >
              &larr;
            </button>
            <h2 className="pl-10 popup-title">Create Notification!</h2>
            <p className="pl-10 popup-subtitle">
              Write what you want to say to your{" "}
              {view === "userNotifications" ? "users" : "vendors"}!
            </p>
            <input
              type="text"
              className="pl-3 popup-input"
              placeholder="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <textarea
              className="popup-textarea"
              placeholder="Write here..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
            ></textarea>
            <div className="popup-send-btn-container">
              <button
                className="popup-send-btn"
                onClick={handleSendNotification}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;
